import { useEffect, useState, type FormEvent } from 'react';
import type { CoinCategory, CoinCity, ActivityType } from '@/lib/types';
import { coinCategories, coinCities, coinCategoryMeta } from '@/pages/le-coin/coinMeta';
import type { SelectedPlace } from '@/lib/types';
import { reverseGeocode, type GeoPoint } from '@/lib/geo';
import PlaceSearchInput from './PlaceSearchInput.jsx';
import LocationPicker from './LocationPicker';

interface PublishModalProps {
  onClose: () => void; onPublishActivity: (data: { type: ActivityType; title: string; description: string; place: SelectedPlace; startsAt?: string; endsAt?: string; maxParticipants?: number; tags: string[] }) => void;

  onPublishCoin: (data: { category: CoinCategory; city: CoinCity; content: string; }) => void;
}

type Tab = 'activity' | 'coin';

const activityTypes: { value: ActivityType; label: string; icon: string }[] = [
  { value: 'SOCIAL', label: 'Sortir', icon: 'ri-cup-line' },
  { value: 'SPORT', label: 'Bouger', icon: 'ri-run-line' },
  { value: 'HELP_REQUEST', label: "Besoin d'aide", icon: 'ri-hand-heart-line' },
  { value: 'HELP_OFFER', label: "Proposition d'aide", icon: 'ri-service-line' },
];

const MAX_COIN_LENGTH = 280;

export default function PublishModal({ onClose, onPublishActivity, onPublishCoin }: PublishModalProps) {
  const [tab, setTab] = useState<Tab>('activity');


  // Empêche la page située derrière la modale de défiler tant que la publication est ouverte.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Activity form
  const [type, setType] = useState<ActivityType>('SOCIAL');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const [locationQuery, setLocationQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(null);

  const [point, setPoint] = useState<GeoPoint | null>(null);
  const [showMap, setShowMap] = useState(false);

  // Coin form
  const [category, setCategory] = useState<CoinCategory>('VU');
  const [city, setCity] = useState<CoinCity>('DOLE');
  const [content, setContent] = useState('');


  const canSubmit = tab === 'activity' ? !!title.trim() : !!content.trim();

  // Verification du remplissage du formulaire pour femrer la modale ou pas 
  const hasFormContent =
    tab === 'activity'
      ? !!(
        title.trim() ||
        description.trim() ||
        startsAt ||
        endsAt ||
        maxParticipants ||
        locationQuery.trim() ||
        tagInput.trim() ||
        tags.length > 0
      )
      : !!content.trim();

  const tagSuggestions = [
    'Football',
    'Running',
    'Randonnée',
    'Café',
    'Balade',
    'Apéro',
    'Soirée',
    'Musique',
    'Jeux',
    'Parents',
  ];

  const addTag = (value: string) => {
    const tag = value.trim();

    if (!tag || tags.length >= 5) return;

    const alreadyExists = tags.some(
      (existingTag) => existingTag.toLowerCase() === tag.toLowerCase()
    );

    if (alreadyExists) {
      setTagInput('');
      return;
    }

    setTags((prev) => [...prev, tag]);
    setTagInput('');
  };

  const removeTag = (tagToRemove: string) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };


  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (tab === 'activity') {
      if (!title.trim()) return;
      onPublishActivity({
        type,
        title: title.trim(),
        description: description.trim(),
        place: selectedPlace,

        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,

        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,

        maxParticipants: maxParticipants ? Number(maxParticipants) : undefined,
        tags
      });
    } else {
      if (!content.trim()) return;
      onPublishCoin({ category, city, content: content.trim() });
    }
  };


  const confirmMapSelection = async () => {
    if (!point) {
      setShowMap(false);
      return;
    }

    const placeName = await reverseGeocode(point);
    const label = placeName ?? `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`;

    setSelectedPlace({
      label,
      latitude: point.latitude,
      longitude: point.longitude,
    });

    setLocationQuery(label);
    setShowMap(false);
  };


  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 max-[430px]:p-0">
      <div className="absolute inset-0 bg-foreground-950/50 animate-fade-in" onClick={() => {
        if (!hasFormContent) {
          onClose();
        }
      }}></div>

      <div className="relative w-full max-w-lg bg-white rounded-lg border border-background-200 animate-scale-in max-h-[90vh] overflow-y-auto max-[430px]:h-[100dvh] max-[430px]:max-h-none max-[430px]:rounded-none max-[430px]:border-0">
        <div className="flex items-center justify-between px-6 py-[.8rem] max-[430px]:py-[0.5rem] border-b border-background-200">
          <h3 className="font-heading text-xl font-semibold text-foreground-950">Publier</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Tab switch */}
        <div className="px-6 pt-4 [@media(max-width:344px)]:px-[0.5rem]">
          <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full">
            <button
              type="button"
              onClick={() => setTab('activity')}
              className={`flex  items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'activity'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-calendar-event-line text-base"></i>
              Créer une activité
            </button>
            {/* 
               ============= Desactivé temporaire =====================
            <button
              type="button"
              onClick={() => setTab('coin')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'coin'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-shield-user-line text-base"></i>
              Le Coin
            </button> */}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 flex flex-col gap-[0.675rem] py-4 max-[430px]:py-2 max-[430px]:px-[1.1rem] max-[430px]:gap-3 max-[344px]:px-[0.5rem]"
        >
          {tab === 'activity' ? (
            <>
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {activityTypes.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap
                         max-[430px]:gap-[0.3rem]
                          max-[430px]:px-[0.55rem]
                          max-[430px]:py-[0.4rem]
                          max-[430px]:text-[0.8rem]
                          max-[430px]:leading-5
                          [@media(min-width:344px)_and_(max-width:375px)]:text-[0.65rem]
                         ${type === opt.value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-background-200 text-foreground-600 hover:border-background-300'
                        }`}
                    >
                      <i className={`${opt.icon} text-base`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="activity-title" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Titre
                </label>
                <input
                  id="activity-title"
                  name="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex : Petit foot du vendredi"
                  className="w-full px-3 py-2.5 max-[430px]:py-[0.425rem] rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="activity-desc" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Description
                </label>
                <textarea
                  id="activity-desc"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Décris ton activité, ce qu'il faut prévoir..."
                  className="w-full px-3 py-2.5 max-[430px]:py-[0.425rem] rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                />
              </div>

              {/* Date de l'activité, fin et participants */}
              <div
                className="
                  grid grid-cols-1 gap-x-[0.3rem]
                  min-[375px]:max-[430px]:grid-cols-[1.45fr_1.45fr_1fr]
                  min-[375px]:max-[430px]:gap-2
                  md:grid-cols-[1fr_1fr_7.5rem]
                "
              >
                {/* Début */}
                <div className="min-w-0">
                  <label
                    htmlFor="activity-starts-at"
                    className="
                      block
                      text-sm
                      font-medium
                      text-foreground-700
                      mb-1.5
                      max-[430px]:text-[0.75rem]
                    "
                  >
                    Début
                  </label>

                  <input
                    id="activity-starts-at"
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    className="
                      w-full
                      px-[0.3rem]
                      py-2.5
                      max-[430px]:px-[0.35rem]
                      max-[430px]:py-[0.425rem]
                      rounded-md
                      border
                      border-background-200
                      text-sm
                      max-[430px]:text-[0.7rem]
                      text-foreground-900
                      focus:outline-none
                      focus:border-primary-400
                      focus:ring-2
                      focus:ring-primary-100
                    "
                  />
                </div>

                {/* Fin */}
                <div className="min-w-0">
                  <label
                    htmlFor="activity-ends-at"
                    className="
                      block
                      text-sm
                      font-medium
                      text-foreground-700
                      mb-1.5
                      max-[430px]:text-[0.75rem]
                    "
                  >
                    Fin
                    <span className="text-foreground-400 font-normal">
                      {' '}(Opt.)
                    </span>
                  </label>

                  <input
                    id="activity-ends-at"
                    type="datetime-local"
                    value={endsAt}
                    min={startsAt || undefined}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="
                      w-full
                      px-[.4rem]
                      py-2.5
                      max-[430px]:px-[0.35rem]
                      max-[430px]:py-[0.425rem]
                      rounded-md
                      border
                      border-background-200
                      text-sm
                      max-[430px]:text-[0.7rem]
                      text-foreground-900
                      focus:outline-none
                      focus:border-primary-400
                      focus:ring-2
                      focus:ring-primary-100
                    "
                  />
                </div>

                {/* Participants */}
                <div className="min-w-0">
                  <label
                    htmlFor="activity-max-participants"
                    className="
                      block
                      text-sm
                      font-medium
                      text-foreground-700
                      mb-1.5
                      max-[430px]:text-[0.65rem]
                      whitespace-nowrap
                    "
                  >
                    Participants
                    <span className="text-foreground-400 font-normal">
                      {' '}(Opt.)
                    </span>
                  </label>

                  <input
                    id="activity-max-participants"
                    type="number"
                    min="2"
                    max="100"
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(e.target.value)}
                    placeholder="Ex : 10"
                    className="
                      w-full
                      px-[.4rem]
                      py-2.5
                      max-[430px]:px-[0.35rem]
                      max-[430px]:py-[0.425rem]
                      rounded-md
                      border
                      border-background-200
                      text-sm
                      max-[430px]:text-[0.7rem]
                      text-foreground-900
                      placeholder:text-foreground-300
                      focus:outline-none
                      focus:border-primary-400
                      focus:ring-2
                      focus:ring-primary-100
                    "
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Lieu <span className="text-foreground-400 font-normal">(public, approximatif)</span>
                </label>

                <div className="flex items-stretch gap-2">
                  <div className="flex-1 min-w-0">
                    <PlaceSearchInput
                      value={locationQuery}
                      onPlaceSelected={(place) => {
                        setSelectedPlace(place);
                        setPoint({
                          latitude: place.latitude,
                          longitude: place.longitude,
                        });
                        setLocationQuery(place.label);
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowMap(true)}
                    className="shrink-0 px-3 rounded-md border border-background-200 text-foreground-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-map-pin-2-line text-base mr-1.5"></i>
                    Carte
                  </button>
                </div>

                {point ? (
                  <p className="mt-1.5 text-xs text-primary-600">
                    <i className="ri-checkbox-circle-fill mr-1"></i>
                    Position sélectionnée : {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
                  </p>
                ) : (type === 'HELP_REQUEST' || type === 'HELP_OFFER') ? (
                  <p className="mt-1.5 text-xs text-primary-600">
                    Tu préfères rester approximatif ? Choisis simplement un point sur la carte.
                  </p>
                ) : null}
              </div>

              {/* Tags */}
              <div>
                <label
                  htmlFor="activity-tags"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Tags{' '}
                  <span className="text-foreground-400 font-normal">
                    (optionnel, max 5)
                  </span>
                </label>

                <div className="flex items-stretch gap-2">
                  <input
                    id="activity-tags"
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag(tagInput);
                      }
                    }}
                    disabled={tags.length >= 5}
                    placeholder={
                      tags.length >= 5
                        ? 'Maximum 5 tags'
                        : 'Tape un tag puis Entrée'
                    }
                    className="
                    flex-1 min-w-0
                    px-3 py-2.5
                    max-[430px]:py-[0.425rem]
                    rounded-md
                    border border-background-200
                    text-sm text-foreground-900
                    placeholder:text-foreground-300
                    focus:outline-none
                    focus:border-primary-400
                    focus:ring-2 focus:ring-primary-100
                    disabled:bg-background-50
                    disabled:cursor-not-allowed
                  "
                  />

                  <button
                    type="button"
                    onClick={() => addTag(tagInput)}
                    disabled={!tagInput.trim() || tags.length >= 5}
                    className="
                    shrink-0
                    px-4
                    rounded-md
                    border border-background-200
                    text-sm font-semibold
                    text-foreground-600
                    hover:bg-background-100
                    transition-colors
                    cursor-pointer
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                  >
                    Ajouter
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="
                    mt-2 flex flex-wrap gap-1.5
                    min-[344px]:max-[430px]:gap-[0.275rem]
                  ">
                    {tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="
                        inline-flex items-center gap-1
                        px-2.5 py-1
                        min-[344px]:max-[430px]:px-[0.425rem]
                        rounded-full
                        bg-primary-50
                        text-primary-700
                        text-xs font-medium
                        hover:bg-primary-100
                        transition-colors
                        cursor-pointer
                       "
                        title="Retirer ce tag"
                      >
                        {tag}
                        <i className="ri-close-line text-sm" />
                      </button>
                    ))}
                  </div>
                )}

                {tags.length < 5 && (
                  <div className="
                    mt-3 flex items-center gap-1.5 flex-wrap
                    min-[344px]:max-[430px]:gap-[0.3rem]
                  ">
                    <span className="text-xs text-foreground-400">
                      Suggestions :
                    </span>

                    {tagSuggestions
                      .filter(
                        (suggestion) =>
                          !tags.some(
                            (tag) =>
                              tag.toLowerCase() === suggestion.toLowerCase()
                          )
                      )
                      .map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => addTag(suggestion)}
                          className="
                          px-2.5 py-1
                          min-[344px]:max-[430px]:px-[0.425rem]
                          rounded-full
                          bg-background-100
                          text-foreground-600
                          text-xs
                          hover:bg-background-200
                          transition-colors
                          cursor-pointer
                        "
                        >
                          {suggestion}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Coin content */}
              <div>
                <label htmlFor="coin-content" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Ton message
                </label>
                <textarea
                  id="coin-content"
                  name="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={4}
                  maxLength={MAX_COIN_LENGTH}
                  placeholder="Un truc vu, une confession, un coup de cœur, une question… Dis tout, personne ne saura que c'est toi."
                  className="w-full px-3 py-2.5 max-[430px]:py-[0.425rem] rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                />
                <span className={`block mt-1 text-right text-xs ${content.length >= MAX_COIN_LENGTH ? 'text-primary-600 font-semibold' : 'text-foreground-400'}`}>
                  {content.length}/{MAX_COIN_LENGTH}
                </span>
              </div>

              {/* Category */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-foreground-500 whitespace-nowrap">Catégorie</span>
                {coinCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${category === cat
                      ? 'bg-foreground-900 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                  >
                    <i className={`${coinCategoryMeta[cat].icon} text-sm`}></i>
                    {coinCategoryMeta[cat].label}
                  </button>
                ))}
              </div>

              {/* City */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-foreground-500 whitespace-nowrap">Ville</span>
                {coinCities.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCity(c.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${city === c.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                  >
                    <i className="ri-map-pin-line text-sm"></i>
                    {c.label}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 max-[430px]:py-[0.425rem] rounded-md border border-background-200 text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 px-4 py-2.5 max-[430px]:py-[0.425rem] rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {tab === 'activity' ? (
                <>
                  <i className="ri-add-line"></i>
                  Publier
                </>
              ) : (
                <>
                  <i className="ri-shield-user-line"></i>
                  Publier anonymement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      {showMap && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setShowMap(false)}
          ></div>

          <div className="relative w-full max-w-md bg-white rounded-lg border border-background-200">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200">
              <h4 className="font-heading text-base font-semibold text-foreground-950">
                Choisir la position
              </h4>

              <button
                type="button"
                onClick={() => setShowMap(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-4">
              <LocationPicker point={point} onChange={setPoint} />
            </div>

            <div className="px-5 py-3.5 border-t border-background-200 flex justify-end">
              <button
                type="button"
                onClick={confirmMapSelection}
                disabled={!point}
                className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}